import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Navigation, Award, Clock } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(isoString) {
  if (!isoString) return 'N/A';
  try {
    var date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return 'N/A';
  }
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    var date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch (e) {
    return dateStr;
  }
}

function getPriorityColor(priority) {
  if (priority === 'urgent') return 'bg-red-500';
  if (priority === 'high') return 'bg-orange-500';
  if (priority === 'medium') return 'bg-yellow-500';
  return 'bg-green-500';
}

function TaskItem(props) {
  var task = props.task;
  var comments = task.comments || [];
  
  var commentElements = [];
  for (var i = 0; i < comments.length; i++) {
    var comment = comments[i];
    commentElements.push(
      <div key={i} className="text-xs text-gray-300 bg-slate-600/50 p-2 rounded mb-1">
        <p>{comment.text}</p>
        <p className="text-gray-500 mt-1">{formatTime(comment.created_at)}</p>
      </div>
    );
  }
  
  return (
    <div className="p-3 bg-slate-700/50 rounded-lg mb-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-white font-medium text-sm">{task.title}</span>
        <Badge className={getPriorityColor(task.priority) + ' text-xs'}>{task.priority}</Badge>
      </div>
      {task.business_name && (
        <p className="text-xs text-gray-400 mb-1">📍 {task.business_name} {task.business_location ? '- ' + task.business_location : ''}</p>
      )}
      {task.procedure_type && (
        <p className="text-xs text-blue-400 mb-1">🔧 {task.procedure_type}</p>
      )}
      {task.description && (
        <p className="text-xs text-gray-300 mb-2">{task.description}</p>
      )}
      {comments.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/10">
          <p className="text-xs text-amber-400 mb-1">💬 Employee Comments:</p>
          {commentElements}
        </div>
      )}
    </div>
  );
}

function TasksList(props) {
  var tasks = props.tasks;
  if (!tasks || tasks.length === 0) {
    return <p className="text-center text-gray-400 py-4">No tasks</p>;
  }
  
  var taskElements = [];
  for (var i = 0; i < tasks.length; i++) {
    taskElements.push(<TaskItem key={tasks[i].id} task={tasks[i]} />);
  }
  return <div>{taskElements}</div>;
}

function DistanceBar(props) {
  var day = props.day;
  var maxKm = props.maxKm;
  var height = day.km > 0 ? Math.max(20, (day.km / maxKm) * 100) : 10;
  
  return (
    <div className="flex-1 flex flex-col items-center">
      <div 
        className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all"
        style={{ height: height + '%' }}
        title={day.km + ' km'}
      ></div>
      <p className="text-xs text-gray-400 mt-1">{formatDate(day.date)}</p>
      <p className="text-xs text-blue-400">{day.km}km</p>
    </div>
  );
}

function DistanceHistory(props) {
  var history = props.history;
  var maxKm = 1;
  for (var i = 0; i < history.length; i++) {
    if (history[i].km > maxKm) maxKm = history[i].km;
  }
  
  var bars = [];
  for (var j = 0; j < history.length; j++) {
    bars.push(<DistanceBar key={j} day={history[j]} maxKm={maxKm} />);
  }
  
  return <div className="flex items-end justify-between gap-2 h-24">{bars}</div>;
}

export function EmployeeMonitoringModal(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var employeeId = props.employeeId;
  var [monitorData, setMonitorData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [activeTaskTab, setActiveTaskTab] = useState('pending');

  useEffect(function() {
    if (isOpen && employeeId) {
      setLoading(true);
      fetch(API_URL + '/api/employee-admin/monitor/' + employeeId)
        .then(function(r) { return r.json(); })
        .then(function(d) { setMonitorData(d); setLoading(false); })
        .catch(function(e) { console.error('Error:', e); setLoading(false); });
    }
  }, [isOpen, employeeId]);

  if (!isOpen) return null;

  var emp = monitorData ? monitorData.employee : null;
  var att = monitorData ? monitorData.attendance : null;
  var loc = monitorData ? monitorData.location : null;
  var dist = monitorData ? monitorData.distance : null;
  var tasks = monitorData ? monitorData.tasks : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Navigation className="w-6 h-6 text-blue-400" />
            Employee Live Monitoring
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : monitorData ? (
          <div className="space-y-4">
            {/* Employee Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg">
              <div>
                <h3 className="text-xl font-bold text-white">{emp.name}</h3>
                <p className="text-gray-400">{emp.phone}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-blue-600">{emp.employee_type}</Badge>
                  {emp.department && <Badge className="bg-slate-600">{emp.department}</Badge>}
                  {monitorData.weekly_badge && (
                    <Badge className={monitorData.weekly_badge === 'gold' ? 'bg-yellow-500' : monitorData.weekly_badge === 'silver' ? 'bg-slate-400' : 'bg-amber-700'}>
                      {monitorData.weekly_badge === 'gold' ? '🥇' : monitorData.weekly_badge === 'silver' ? '🥈' : '🥉'} {monitorData.weekly_badge}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right">
                {att.is_working ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-medium">Currently Working</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="text-gray-400">Not Working</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className={"grid gap-3 " + (emp.employee_type === 'fleet_manager' ? "grid-cols-2 md:grid-cols-4" : "grid-cols-3")}>
              {emp.employee_type === 'fleet_manager' && (
                <Card className="bg-slate-700/50 border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">{dist.today_km}</p>
                    <p className="text-xs text-gray-400">KM Today</p>
                  </CardContent>
                </Card>
              )}
              <Card className="bg-slate-700/50 border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{tasks.completed_count}</p>
                  <p className="text-xs text-gray-400">Completed</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-700/50 border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-400">{tasks.pending_count}</p>
                  <p className="text-xs text-gray-400">Pending</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-700/50 border-white/10">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-purple-400">{tasks.processing_count}</p>
                  <p className="text-xs text-gray-400">Processing</p>
                </CardContent>
              </Card>
            </div>

            {/* Time & Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="bg-slate-700/50 border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-5 h-5 text-green-400" />
                    <span className="text-white font-medium">Working Time</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">{formatTime(att.check_in_time)}</p>
                      <p className="text-xs text-gray-400">Check In</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-400">{att.working_duration || 'N/A'}</p>
                      <p className="text-xs text-gray-400">Duration</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{formatTime(att.check_out_time)}</p>
                      <p className="text-xs text-gray-400">Check Out</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-700/50 border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Navigation className="w-5 h-5 text-blue-400" />
                    <span className="text-white font-medium">Current Location</span>
                  </div>
                  {loc.current ? (
                    <div>
                      <p className="text-lg font-mono text-blue-400">
                        {loc.current.latitude.toFixed(6)}, {loc.current.longitude.toFixed(6)}
                      </p>
                      {loc.last_updated && (
                        <p className="text-xs text-gray-400 mt-1">Last updated: {formatTime(loc.last_updated)}</p>
                      )}
                      <a 
                        href={'https://www.google.com/maps?q=' + loc.current.latitude + ',' + loc.current.longitude}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 text-xs text-blue-400 hover:text-blue-300 underline"
                      >
                        View on Google Maps →
                      </a>
                    </div>
                  ) : (
                    <p className="text-gray-400">Location not available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Distance History - Only for Fleet Managers */}
            {emp.employee_type === 'fleet_manager' && (
              <Card className="bg-slate-700/50 border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="w-5 h-5 text-amber-400" />
                    <span className="text-white font-medium">15-Day Distance History</span>
                  </div>
                  <DistanceHistory history={dist.history} />
                </CardContent>
              </Card>
            )}

            {/* Tasks Section */}
            <Card className="bg-slate-700/50 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-medium">Tasks & Comments</span>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant={activeTaskTab === 'pending' ? 'default' : 'ghost'}
                      onClick={function() { setActiveTaskTab('pending'); }}
                      className={activeTaskTab === 'pending' ? 'bg-amber-600' : 'text-gray-400'}
                    >
                      Pending ({tasks.pending_count})
                    </Button>
                    <Button 
                      size="sm" 
                      variant={activeTaskTab === 'processing' ? 'default' : 'ghost'}
                      onClick={function() { setActiveTaskTab('processing'); }}
                      className={activeTaskTab === 'processing' ? 'bg-purple-600' : 'text-gray-400'}
                    >
                      Processing ({tasks.processing_count})
                    </Button>
                    <Button 
                      size="sm" 
                      variant={activeTaskTab === 'completed' ? 'default' : 'ghost'}
                      onClick={function() { setActiveTaskTab('completed'); }}
                      className={activeTaskTab === 'completed' ? 'bg-green-600' : 'text-gray-400'}
                    >
                      Completed ({tasks.completed_count})
                    </Button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {activeTaskTab === 'pending' && <TasksList tasks={tasks.pending} />}
                  {activeTaskTab === 'processing' && <TasksList tasks={tasks.processing} />}
                  {activeTaskTab === 'completed' && <TasksList tasks={tasks.completed} />}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="text-center text-gray-400 py-8">Unable to load employee data</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EmployeeMonitoringModal;
